import React from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { getCultureCodeFromLocalStorage } from '../../helpers/cache';
import { Link } from '@reach/router';
import { useTheme } from '@emotion/react';
/** @jsxImportSource @emotion/react */
import { css, jsx } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';
import { selectImagePath } from '../../reducers/ui';
import RememberMeInput from '../../components/form/RememberMeInput';
import FormTextField from '../../components/form/FormTextField';
import Button from '../../components/button/Button';
import { selectCultureCode } from '../../reducers/language';
import { selectAuthMessage, selectAuthStatus, signIn, SignInAttempt } from '../../reducers/auth';
import { AppPath, MAINTENANCE_WARNING } from '../../appConstants';
import { SignInTranslation } from './signInTypes';
import { Spinner } from 'components/spinner/Spinner';
import { getErrorMessage } from 'errors';
import { ReactComponent as WarningIcon } from 'assets/ui-update/warning.svg';
import { useTranslation } from 'react-i18next';

type Props = {
    translations: SignInTranslation | null;
};

const SignInForm: React.FC<Props> = ({ translations }) => {
    const { t } = useTranslation('auth');
    const authMessages = useSelector(selectAuthMessage);
    const authMessageToShow =
        authMessages && authMessages.length > 0 && getErrorMessage(authMessages[0].messageCode);
    const authStatus = useSelector(selectAuthStatus);

    const { values } = useFormikContext<SignInAttempt>();

    const theme = useTheme();
    const imagesPath = useSelector(selectImagePath);

    const cultureCode = useSelector(selectCultureCode);

    const SignInFormCSS = css`
        .Btn {
            background: ${theme.colors.first};
        }

        a {
            color: ${theme.colors.second};
        }

        input[type='checkbox']:checked + .CustomInput:after {
            background-image: url('../..${imagesPath}/Icon_Tick.png');
        }
    `;

    return (
        <Form className="WidthContent AuthFormContainer">
            <div className="AuthForm" css={SignInFormCSS}>
                <h1>{translations?.heading1 ?? ''}</h1>
                <p>{translations?.sub_heading1 ?? ''}</p>
                <div className="FieldsContainer">
                    <FormTextField
                        field={'username'}
                        label={t('emailAddress')}
                        required={true}
                        maxLength={200}
                        className="notranslate"
                    />
                    <FormTextField
                        field={'password'}
                        label={t('password')}
                        type={'password'}
                        required={true}
                        maxLength={20}
                        className="notranslate"
                    />
                    {authMessageToShow && (
                        <span className="SignInErrorMessage">{authMessageToShow}</span>
                    )}
                    <div className="RememberMe">
                        <RememberMeInput
                            type="checkbox"
                            name="rememberMe"
                            id="RememberMe"
                            checked={values.rememberMe}
                        />
                        <label className="CustomInput notranslate" translate="no" htmlFor="RememberMe">
                            {t('rememberMe')}
                        </label>
                        <Link to={`/${cultureCode}${AppPath.FORGOTTEN_PASSWORD}`}>
                            <span className="ForgottenPassword notranslate" translate="no">
                                {t('forgotPassword')}
                            </span>
                        </Link>
                    </div>
                    <Button variety="full" type="submit" disabled={authStatus === 'pending'} className="notranslate" translate="no">
                        {t('signIn')}
                    </Button>
                    {authStatus === 'pending' && <Spinner className="noBG-absCenter" />}


                    {MAINTENANCE_WARNING?.ACTIVE ? 

                    <div className='AlertNotification'>
                        
                        <WarningIcon className='WarningIcon'/>

                        <div className='NotificationText'>
                            <h3>{MAINTENANCE_WARNING.HEADER_MESSAGE}</h3>
                            <span className='SubText'>{MAINTENANCE_WARNING.SUBTEXT_MESSAGE}</span>
                        </div>
                    </div>
                    :
                    <></>
                    }
                    

                    <div className="Redirect notranslate" translate="no">
                        <span>{t('noAccount')}&nbsp;</span>
                        <Link to={`/${cultureCode}${AppPath.REGISTER}`}>
                            {' '}
                            <span className="Register">{t('registerLink')}</span>
                        </Link>
                    </div>

                    
                </div>
            </div>
        </Form>
    );
};

export default SignInForm;
